import * as React from 'react';

import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { useGetParking } from 'src/features/parkingManagement/api/useGetParking';
import { useListPoints } from 'src/features/points/api/useListPoints';
import CellNormalCardEnter from 'src/features/points/components/tables/cells/CellNormalCardEnter';
import { type Point, PointType } from 'src/features/points/types';
import pointsMessages from 'src/features/points/utils/messages';
import usePagination from 'src/hooks/usePagination';
import useSearch from 'src/hooks/useSearch';
import {
    DataTable,
    DataTableColumnHeader,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import { useTitleStore } from 'src/stores/useTitleStore';

const columnHelper = createColumnHelper<Point>();

function PointsTable() {
    const { parkingId } = useParams();
    const { formatMessage } = useIntl();
    const { setTitle, removeTitle } = useTitleStore();

    const { queryPaginationOptions, ...pagination } = usePagination();
    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'parking-points' });

    const {
        data: pointsData,
        isLoading,
        isError,
        refetch,
    } = useListPoints({
        ...queryPaginationOptions,
        ...querySearchOptions,
        parkingId: parkingId || '',
        config: { enabled: !!parkingId },
    });

    const { data } = useGetParking({ parkingId: parkingId || '', config: { enabled: !!parkingId } });

    React.useEffect(() => {
        if (!data) return;
        setTitle(data.name);
        return () => removeTitle();
    }, [data, formatMessage]);

    const columns = [
        columnHelper.accessor('name', {
            header: DataTableColumnHeader,
            meta: {
                title: formatMessage(pointsMessages.name),
            },
        }),
        columnHelper.accessor('type', {
            header: DataTableColumnHeader,
            meta: {
                title: formatMessage(pointsMessages.type),
            },
            cell: ({ row }) => (
                <span className="whitespace-nowrap">
                    {formatMessage(row.original.type === PointType.EXIT ? pointsMessages.exit : pointsMessages.entry)}
                </span>
            ),
        }),
        columnHelper.accessor('camera_number', {
            header: DataTableColumnHeader,
            meta: {
                title: formatMessage(pointsMessages.cameraNumber),
            },
        }),
        columnHelper.accessor('price_plan_name', {
            header: DataTableColumnHeader,
            meta: {
                title: formatMessage(pointsMessages.pricePlan),
            },
            cell: ({ row }) => row.original.price_plan_name || '-',
        }),
        columnHelper.accessor('normal_card_enter', {
            header: DataTableColumnHeader,
            meta: {
                title: formatMessage(pointsMessages.allowNormalCards),
            },
            cell: ({ row }) => <CellNormalCardEnter point={row.original} onUpdated={refetch} />,
        }),
    ];

    return (
        <DataTable
            columns={columns}
            data={pointsData}
            pagination={pagination}
            search={search}
            semantics={{
                title: formatMessage(pointsMessages.points),
                entryName: formatMessage(pointsMessages.point),
                entryNames: formatMessage(pointsMessages.points),
            }}
            isError={isError}
            isLoading={isLoading}
            refetch={refetch}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
            }}
            enableLocalFilters={true}
        />
    );
}

export default PointsTable;
